import {useCallback, useEffect, useState} from "react";
import {v4 as uuidv4} from "uuid";

import {BaseModalArgs, ConfirmModal} from "..";
import {ModalData} from "../new";
import {Post, ShuffledComment} from "../../data";
import {Tab} from "../../components/tabs/tab";
import {ShuffleCaption, ShuffleCommentChange} from "./shuffle-caption";
import {ShuffleImage} from "./shuffle-image";

import styles from "./shuffle-content.module.scss";

export interface ShuffleContentModalArgs<T> extends BaseModalArgs<T> {
	post: Post;
	onConfirm: (comments: ShuffledComment[], images?: string[]) => void;
	disabled: boolean;
	modal: ModalData;
}

const loadComments = post =>
	[
		...post.shuffledComments.map(c => ({
			service: "GENERAL",
			shuffle: c,
			aiChat: c.isAIGenerated
				? undefined
				: {
						id: uuidv4(),
						message: "",
						prompt: c.openGraph.comment,
						service: "GENERAL",
				  },
		})),
	] as ShuffleCommentChange[];

const loadImages = post => {
	const images = [...(post?.shuffledImages?.map(({image}) => image) || [])];

	const postImage = post?.opengraphs?.general?.image;

	if (postImage && !images.includes(postImage)) {
		images.push(postImage);
	}

	return images;
};

export const ShuffleContentModal = <T,>({post, onConfirm, disabled, modal}: ShuffleContentModalArgs<T>) => {
	const [selectedTab, setSelectedTab] = useState<"caption" | "image">("caption");

	const [comments, setComments] = useState(() => loadComments(post));
	const [images, setImages] = useState(() => loadImages(post));

	const reloadComments = useCallback(() => setComments(() => loadComments(post)), [post]);
	const reloadImages = useCallback(() => setImages(() => loadImages(post)), [post]);

	const [validShuffleContent, setValidShuffleContent] = useState(true);

	const prompt =
		post.opengraphs.general.comment ||
		post.opengraphs.general.title ||
		post.opengraphs.general.description ||
		"";

	const handleConfirm = close => {
		if (!validShuffleContent) return;

		const general = comments.filter(c => c.service === "GENERAL").map(c => c.shuffle);

		onConfirm(general, images);
		close();
	};

	useEffect(() => {
		if (modal.open) {
			reloadComments();
			reloadImages();
		}
	}, [modal.open, reloadComments, reloadImages]);

	return (
		<ConfirmModal onConfirm={handleConfirm} title="Post Content" modal={modal}>
			<div className={styles.container}>
				<div className={styles.tabs}>
					<Tab
						onClick={() => setSelectedTab("caption")}
						active={selectedTab === "caption"}
						title="Caption Shuffle"
					/>
					<Tab
						onClick={() => setSelectedTab("image")}
						active={selectedTab === "image"}
						title="Image Shuffle"
					/>
				</div>
				<div className={styles.content}>
					{selectedTab === "caption" && (
						<ShuffleCaption
							prompt={prompt}
							disabled={disabled}
							comments={comments}
							setComments={setComments}
							setValid={setValidShuffleContent}
						/>
					)}
					{selectedTab === "image" && (
						<ShuffleImage disabled={disabled} images={images} setImages={setImages} />
					)}
				</div>
			</div>
		</ConfirmModal>
	);
};
